import type {Command, EditorView, KeyBinding} from '@codemirror/view'
import {ActionBar, type IconButtonProps} from '@primer/react'
import {useMemo} from 'react'

import type {FormatNodeType} from '../codemirror/commands/format-types'
import {useSelectionContext} from '../context/SelectionContext'

interface ToolbarButtonProps
  extends Omit<
    IconButtonProps,
    'onClick' | 'keybindingHint' | 'onMouseDown' | 'variant' | 'aria-labelledby' | 'aria-pressed'
  > {
  view: EditorView | null
  command: KeyBinding | Command
  formatNodeType?: FormatNodeType
  formatNodeSubType?: number
  ['aria-label']: string
}

function buildKeybindingHint(keybinding: KeyBinding) {
  return keybinding.key?.replaceAll('+', 'Plus')?.replaceAll('-', '+')
}

export const ToolbarButton = ({command, view, formatNodeType, formatNodeSubType, ...props}: ToolbarButtonProps) => {
  const commandIsKeybinding = typeof command === 'object'
  const commandFn = commandIsKeybinding ? command.run : command

  const selectionContext = useSelectionContext()
  const isPressed = useMemo(() => {
    if (!selectionContext || !formatNodeType) return false
    if (formatNodeType === 'heading') {
      return [...selectionContext].some(
        node => node.type === 'heading' && 'depth' in node && node.depth === formatNodeSubType,
      )
    }
    return [...selectionContext].some(node => node.type === formatNodeType)
  }, [selectionContext, formatNodeType, formatNodeSubType])

  return (
    <ActionBar.IconButton
      variant="invisible"
      // Prevent focus leaving input:
      onMouseDown={e => e.preventDefault()}
      keybindingHint={commandIsKeybinding ? buildKeybindingHint(command) : undefined}
      disabled={!view}
      onClick={() => view && commandFn?.(view)}
      aria-pressed={isPressed}
      {...props}
    />
  )
}

ToolbarButton.displayName = 'MarkdownEditor.ToolbarButton'
